import React, {Fragment, useCallback, useEffect, useRef, useState} from "react";
import Breadcrumb from "../../../../layout/breadcrumb";
import {Container} from "reactstrap";
import {useDispatch, useSelector} from "react-redux";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";
import {useHistory, useParams} from "react-router-dom";
import PreLoader from "../../../../share-components/ui/preLoader";
import withReducer from "../../../../store/withReducer";
import leadReducer from "../../store";
import {
    addBuyerLead,
    closeNewAddressDialog,
    deleteBuyerLeadProfileImage,
    getBuyerLeadSettings,
    getSelectedBuyerLeadData,
    openNewAddressDialog,
    openEditAddressDialog,
    uploadBuyerLeadProfileImage,
    setSettings,
} from "../../store/buyerLeadSlice";
import moment from "moment";
import {toast} from "react-toastify";
import SweetAlert from "sweetalert2";
import "./style.scss";
import BuyerLeadHeader, { BUYER_LEAD_SECTION_IDS } from "./buyerLeadHeader";
import BuyerLeadProfile from "./buyerLeadProfile";
import BuyerLeadCommerce from "./buyerLeadCommerce";
import BuyerLeadEngagement from "./buyerLeadEngagement";
import LogActivity from "../../../log";
import ModalFormSubmit from "../../../../share-components/form/modalFormSubmit";
import BuyerLeadAddressForm from "../../form/buyerLeadAddressForm";
import UpdatePrimaryBuyerLead from "../updatePrimaryBuyerLead";

const dataMenu = [
    {
        type: 1,
        text: "Buyer Leads",
        link: "/leads/buyer-lead"
    },
    {
        type: 0,
        text: "View Buyer Lead",
        link: ""
    }
];

const DEFAULT_BUYER_AVATAR = require("../../../../assets/images/user/user.png");

const resolveAvatarImage = (lead) =>
    lead?.avatar ||
    lead?.photo_url ||
    lead?.profile_image ||
    lead?.image ||
    "";

const fallbackValue = (value) => {
    if (value === null || value === undefined || value === "") {
        return "-";
    }
    if (typeof value === "object" && value.name) {
        return value.name;
    }
    return value;
};

const phoneRegExp = /^[0-9]{10}$/;

yup.addMethod(yup.string, "customPhoneValidation", function (errorMessage) {
    return this.test(`test-card-length`, errorMessage, function (value) {
        if (value) {
            return value.match(phoneRegExp) ? true : false;
        }
        return false;
    });
});

const defaultAddressValues = {
    first_name: "",
    last_name: "",
    company: "",
    address_1: "",
    address_2: "",
    city: "",
    zip: "",
    country: "",
    phone_number: "",
    province: "",
};

const addressSchema = yup.object().shape({
    address_1: yup.string().required("Address line 1 is required").typeError("Address line 1 is required"),
    city: yup.string().required("City is required").typeError("City is required"),
    zip: yup.string().required("Zip is required").typeError("Zip is required"),
    country: yup.object().required("Country is required field").typeError("Please select Country"),
    phone_number: yup
        .string()
        .min(9, "Invalid Phone number")
        .max(10, "Invalid Phone number")
        .required("Phone number is required field")
        .typeError("Phone number is required field")
        .customPhoneValidation("You must enter valid phone number"),
    province: yup.object().when(["country"], (country) => {
        if (country) {
            if (country.id !== 242) {
                return yup.string().nullable();
            } else {
                return yup.string().required("State is required");
            }
        } else {
            return yup.string().nullable();
        }
    }),
});

const ViewBuyerLead = () => {
    const {id} = useParams();
    const history = useHistory();
    const dispatch = useDispatch();
    const buyerLeadState = useSelector(({lead}) => lead.buyerLead);
    const currentUser = useSelector(({authReducer}) => authReducer.data.user);
    const [buyerLead, setBuyerLead] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState("customer-details");
    const [addressSaving, setAddressSaving] = useState(false);
    const [avatarUploading, setAvatarUploading] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [copiedAddressIndexes, setCopiedAddressIndexes] = useState([]);
    const scrollContainerRef = useRef(null);
    const headerRef = useRef(null);
    const isScrollingProgrammaticallyRef = useRef(false);

    const {
        handleSubmit: addressHandleSubmit,
        formState: addressFormState,
        control: addressControl,
        reset: addressReset,
        register: addressRegister,
        trigger: addressTrigger,
        setValue: addressSetValue,
        getValues: addressGetValues,
    } = useForm({
        mode: "onChange",
        defaultValues: defaultAddressValues,
        resolver: yupResolver(addressSchema),
    });

    const {errors: addressErrors} = addressFormState;

    const hasViewPermission =
        currentUser?.permission?.includes("Buyer Lead") ?? false;

    const loadBuyerLead = useCallback(() => {
        if (!hasViewPermission) {
            setLoading(false);
            return Promise.resolve();
        }
        if (!id) {
            setLoading(false);
            return Promise.resolve();
        }
        setLoading(true);
        return dispatch(getSelectedBuyerLeadData(id)).then((res) => {
            if (!res.error) {
                setBuyerLead(res.payload);
                setCopiedAddressIndexes([]);
            }
            setLoading(false);
            return res;
        });
    }, [dispatch, id, hasViewPermission]);

    useEffect(() => {
        if (!currentUser) {
            return;
        }
        if (!hasViewPermission) {
            history.push("/error/permission");
            return;
        }
        loadBuyerLead();
    }, [currentUser, hasViewPermission, history, loadBuyerLead]);

    useEffect(() => {
        if (!hasViewPermission) {
            return;
        }
        dispatch(getBuyerLeadSettings()).then((res) => {
            if (!res.error) {
                dispatch(setSettings(res.payload));
            }
        });
    }, [dispatch, hasViewPermission]);

    useEffect(() => {
        if (typeof window === "undefined" || typeof document === "undefined") {
            return;
        }

        const scrollEl = scrollContainerRef.current;
        const headerEl = headerRef.current;

        if (!scrollEl || !headerEl) {
            return;
        }

        const updateStickyMetrics = () => {
            const pageHeader = document.querySelector(".page-header");
            const pageHeaderHeight = pageHeader ? pageHeader.getBoundingClientRect().height : 0;
            const headerHeight = headerEl.getBoundingClientRect().height || 0;

            scrollEl.style.setProperty("--buyer-lead-sticky-offset", `${pageHeaderHeight}px`);
            scrollEl.style.setProperty("--buyer-lead-header-height", `${headerHeight}px`);
        };

        updateStickyMetrics();

        if (typeof ResizeObserver === "undefined") {
            window.addEventListener("resize", updateStickyMetrics);
            return () => {
                window.removeEventListener("resize", updateStickyMetrics);
            };
        }

        const resizeObserver = new ResizeObserver(() => {
            updateStickyMetrics();
        });

        resizeObserver.observe(headerEl);
        const pageHeader = document.querySelector(".page-header");
        if (pageHeader) {
            resizeObserver.observe(pageHeader);
        }

        window.addEventListener("resize", updateStickyMetrics);

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener("resize", updateStickyMetrics);
        };
    }, []);

    useEffect(() => {
        if (typeof window === "undefined" || isEditMode || !buyerLead) return;

        const onScroll = () => {
            if (isScrollingProgrammaticallyRef.current) return;

            const scrollEl = scrollContainerRef.current;
            const headerEl = headerRef.current;
            if (!scrollEl || !headerEl) return;

            const computed = window.getComputedStyle(scrollEl);
            const stickyOffset = parseFloat(computed.getPropertyValue("--buyer-lead-sticky-offset")) || 0;
            const headerHeight = parseFloat(computed.getPropertyValue("--buyer-lead-header-height")) || 0;
            const fallbackHeader = headerEl.getBoundingClientRect().height || 0;
            const offset = stickyOffset + (headerHeight || fallbackHeader) + 16;

            let current = null;
            for (const id of BUYER_LEAD_SECTION_IDS) {
                const el = document.getElementById(`buyer-lead-section-${id}`);
                if (el && el.getBoundingClientRect().top <= offset) current = id;
            }
            if (current) setActiveTab((prev) => (prev !== current ? current : prev));
        };

        window.addEventListener("scroll", onScroll, { passive: true });
        onScroll();
        return () => window.removeEventListener("scroll", onScroll);
    }, [isEditMode, buyerLead]);

    const fullName = (() => {
        const name = [buyerLead?.first_name, buyerLead?.last_name].filter(Boolean).join(" ");
        return name || buyerLead?.name || "-";
    })();

    const email = buyerLead?.contact_email || buyerLead?.company_email || buyerLead?.email;
    const phone = buyerLead?.mobile || buyerLead?.phone_number || buyerLead?.mobile_number;
    const emailValue = fallbackValue(email);
    const phoneValue = fallbackValue(phone);
    const isVipMember =
        buyerLead?.is_vip_customer === 1 ||
        buyerLead?.is_vip_customer === "1" ||
        buyerLead?.is_vip_customer === true;
    const vipCustomer = isVipMember ? "Yes" : "No";
    const vipValidityDate =
        buyerLead?.vip_validity ||
        buyerLead?.vip_valid_until ||
        buyerLead?.vip_expiry_date ||
        null;
    const leadCode =
        buyerLead?.customer_code ||
        buyerLead?.code ||
        buyerLead?.reference ||
        (buyerLead?.id ? `BL-${buyerLead.id}` : id ? `BL-${id}` : "-");
    const avatarImage = resolveAvatarImage(buyerLead);
    const avatarUrl = avatarImage || DEFAULT_BUYER_AVATAR;
    const hasCustomAvatar = Boolean(avatarImage);
    const memberSince =
        buyerLead?.member_since ||
        buyerLead?.created_at ||
        buyerLead?.createdAt ||
        buyerLead?.created_date;
    const lastUpdated =
        buyerLead?.last_updated ||
        buyerLead?.updated_at ||
        buyerLead?.updatedAt ||
        buyerLead?.updated_date;
    const dobValue =
        buyerLead?.date_of_birth ||
        buyerLead?.dob ||
        buyerLead?.birth_date ||
        buyerLead?.birthDate;
    const orderHistory = Array.isArray(buyerLead?.orders)
        ? buyerLead.orders
        : Array.isArray(buyerLead?.order_history)
            ? buyerLead.order_history
            : [];
    const parseActivityMoment = (value) => {
        const parsed = moment(value);
        return parsed.isValid() ? parsed : null;
    };
    const lastOrderMoment = orderHistory.reduce((latest, order) => {
        const candidate = parseActivityMoment(
            order?.date ||
                order?.order_date ||
                order?.created_at ||
                order?.createdAt
        );
        if (!candidate) {
            return latest;
        }
        if (!latest || candidate.isAfter(latest)) {
            return candidate;
        }
        return latest;
    }, null);
    const lastActivityMoment = [
        lastOrderMoment,
        parseActivityMoment(buyerLead?.last_activity_at),
        parseActivityMoment(buyerLead?.last_active_at),
        parseActivityMoment(buyerLead?.last_seen_at),
        parseActivityMoment(buyerLead?.last_login_at),
        parseActivityMoment(buyerLead?.last_order_date),
        parseActivityMoment(buyerLead?.last_order_at),
        parseActivityMoment(buyerLead?.last_purchase_at),
        parseActivityMoment(buyerLead?.last_purchase_date),
        parseActivityMoment(lastUpdated),
    ].reduce((latest, candidate) => {
        if (!candidate) {
            return latest;
        }
        if (!latest || candidate.isAfter(latest)) {
            return candidate;
        }
        return latest;
    }, null);
    const activityStatus = (() => {
        if (!lastActivityMoment) {
            return null;
        }
        const daysSince = moment().diff(lastActivityMoment, "days");
        return daysSince <= 30 ? "Active" : "Inactive";
    })();
    const accountStatus = (() => {
        if (activityStatus) {
            return activityStatus;
        }
        const rawStatus = buyerLead?.account_status ?? buyerLead?.status ?? buyerLead?.is_active;
        if (rawStatus === null || rawStatus === undefined || rawStatus === "") {
            return "-";
        }
        const normalized = String(rawStatus).toLowerCase();
        if (rawStatus === 1 || rawStatus === "1" || rawStatus === true || normalized === "active") {
            return "Active";
        }
        if (rawStatus === 0 || rawStatus === "0" || rawStatus === false || normalized === "inactive") {
            return "Inactive";
        }
        return rawStatus;
    })();

    const emailConsent =
        buyerLead?.email_consent === 1 || buyerLead?.email_consent === "1" || buyerLead?.email_consent === true;
    const smsConsent =
        buyerLead?.sms_consent === 1 || buyerLead?.sms_consent === "1" || buyerLead?.sms_consent === true;
    const pushConsent =
        buyerLead?.push_disabled === 1 ||
        buyerLead?.push_disabled === "1" ||
        buyerLead?.push_disabled === true ||
        buyerLead?.push_notifications === 0
            ? false
            : buyerLead?.push_notifications === 1 || buyerLead?.push_notifications === "1";
    const emailConsentDate =
        buyerLead?.email_consent_date ||
        buyerLead?.email_consent_at ||
        buyerLead?.email_marketing_consent_date;
    const smsConsentDate =
        buyerLead?.sms_consent_date ||
        buyerLead?.sms_consent_at ||
        buyerLead?.sms_marketing_consent_date;

    const tags = (() => {
        if (!buyerLead?.tags) {
            return [];
        }
        if (Array.isArray(buyerLead.tags)) {
            return buyerLead.tags
                .map((tag) => (typeof tag === "string" ? tag : tag?.name || tag?.label))
                .filter(Boolean);
        }
        if (typeof buyerLead.tags === "string") {
            return buyerLead.tags
                .split(",")
                .map((tag) => tag.trim())
                .filter(Boolean);
        }
        return [];
    })();

    const summaryBadges = (() => {
        const badges = [];
        if (vipCustomer === "Yes") {
            badges.push({label: "VIP Member", color: "primary"});
        }
        if (buyerLead?.high_value === 1 || buyerLead?.high_value === true) {
            badges.push({label: "High Value", color: "info"});
        }
        if (buyerLead?.email_consent === 1 || buyerLead?.email_consent === true) {
            badges.push({label: "Email Consent", color: "success"});
        }
        if (buyerLead?.sms_consent === 1 || buyerLead?.sms_consent === true) {
            badges.push({label: "SMS Consent", color: "success"});
        }
        if (
            buyerLead?.push_disabled === 1 ||
            buyerLead?.push_disabled === true ||
            buyerLead?.push_notifications === 0
        ) {
            badges.push({label: "Push Disabled", color: "secondary"});
        }
        return badges;
    })();

    const addresses = buyerLead?.address_array || [];
    const countries = buyerLeadState?.settings?.countries || [];

    const pickFirstValue = (...values) =>
        values.find((value) => value !== undefined && value !== null && value !== "");

    const resolveCountry = (address) => {
        const rawCountry = pickFirstValue(
            address?.country,
            address?.country_id,
            address?.countryId,
            address?.country_code,
            address?.countryCode,
            address?.country_name
        );
        if (!rawCountry) {
            return "";
        }
        if (typeof rawCountry === "object") {
            return rawCountry;
        }
        const normalized = String(rawCountry).toLowerCase();
        const match = countries.find((country) => {
            if (String(country.id) === String(rawCountry)) {
                return true;
            }
            return (
                country.name?.toLowerCase() === normalized ||
                country.code?.toLowerCase() === normalized ||
                country.iso2?.toLowerCase() === normalized ||
                country.iso3?.toLowerCase() === normalized
            );
        });
        if (match) {
            return match;
        }
        return {
            id: rawCountry,
            name: String(rawCountry),
            dial_code: address?.dial_code || address?.dialCode,
        };
    };

    const normalizeAddressForForm = (address) => ({
        ...address,
        first_name:
            pickFirstValue(address?.first_name, address?.firstname, address?.firstName, address?.first) || "",
        last_name:
            pickFirstValue(address?.last_name, address?.lastname, address?.lastName, address?.last) || "",
        company:
            pickFirstValue(address?.company, address?.company_name, address?.companyName) || "",
        address_1:
            pickFirstValue(
                address?.address_1,
                address?.address1,
                address?.address_line_1,
                address?.addressLine1
            ) || "",
        address_2:
            pickFirstValue(
                address?.address_2,
                address?.address2,
                address?.address_line_2,
                address?.addressLine2
            ) || "",
        city: pickFirstValue(address?.city, address?.town) || "",
        province: pickFirstValue(address?.province, address?.state, address?.region) || "",
        zip: pickFirstValue(address?.zip, address?.postcode, address?.postal_code) || "",
        phone_number:
            pickFirstValue(address?.phone_number, address?.phone, address?.mobile, address?.mobile_number) || "",
        country: resolveCountry(address),
    });

    const createAddressCopy = (address) => {
        if (!address) {
            return null;
        }
        const {
            shopify_address_id,
            address_id,
            id: addressId,
            is_default,
            default: defaultFlag,
            is_default_address,
            addressIndex: _ai,
            ...rest
        } = address;
        return {
            ...rest,
            is_default: 0,
            is_default_address: 0,
            default: 0,
        };
    };

    const normalizeDefaultFlags = (address, isDefault) => ({
        ...address,
        is_default: isDefault ? 1 : 0,
        is_default_address: isDefault ? 1 : 0,
        default: isDefault ? 1 : 0,
    });

    const resolveDefaultIndex = (list, preferredIndex) => {
        if (!Array.isArray(list) || list.length === 0) {
            return -1;
        }
        if (typeof preferredIndex === "number" && preferredIndex >= 0 && preferredIndex < list.length) {
            return preferredIndex;
        }
        const existingIndex = list.findIndex(
            (item) =>
                item?.is_default === 1 ||
                item?.is_default === "1" ||
                item?.default === 1 ||
                item?.default === "1" ||
                item?.is_default_address === 1 ||
                item?.is_default_address === "1"
        );
        return existingIndex >= 0 ? existingIndex : 0;
    };

    const applyDefaultIndex = (list, defaultIndex) =>
        list.map((address, index) => normalizeDefaultFlags(address, index === defaultIndex));

    const updateCopiedIndexesAfterRemoval = (removedIndex) =>
        setCopiedAddressIndexes((prev) =>
            prev
                .filter((value) => value !== removedIndex)
                .map((value) => (value > removedIndex ? value - 1 : value))
        );

    const onEditAddress = (address, index) => {
        if (!address) {
            return;
        }
        const normalized = normalizeAddressForForm(address);
        dispatch(openEditAddressDialog({data: {...normalized, addressIndex: index}}));
    };

    const onAddAddress = () => {
        if (addressSaving) {
            return;
        }
        dispatch(openNewAddressDialog());
    };

    const onCopyAddress = (address) => {
        if (!address || addressSaving) {
            return;
        }
        const currentAddresses = Array.isArray(buyerLead?.address_array)
            ? [...buyerLead.address_array]
            : [];
        const copiedAddress = createAddressCopy(address);
        if (!copiedAddress) {
            return;
        }
        const updatedAddresses = [...currentAddresses, copiedAddress];
        const copyIndex = updatedAddresses.length - 1;
        const leadId = buyerLead?.id || id;
        if (!leadId) {
            setBuyerLead((prev) =>
                prev
                    ? {
                          ...prev,
                          address_array: updatedAddresses,
                      }
                    : prev
            );
            setCopiedAddressIndexes((prev) => [...prev, copyIndex]);
            toast.success("Address copied successfully", {
                position: toast.POSITION.TOP_RIGHT,
            });
            return;
        }
        const payload = {
            id: leadId,
            formData: updatedAddresses,
            type: 2,
        };
        setAddressSaving(true);
        dispatch(addBuyerLead({...payload})).then((res) => {
            setAddressSaving(false);
            if (!res?.error) {
                setBuyerLead((prev) =>
                    prev
                        ? {
                              ...prev,
                              address_array: updatedAddresses,
                          }
                        : prev
                );
                setCopiedAddressIndexes((prev) => [...prev, copyIndex]);
                toast.success("Address copied successfully", {
                    position: toast.POSITION.TOP_RIGHT,
                });
            } else {
                toast.error(res?.payload?.data || "Failed to copy address", {
                    position: toast.POSITION.TOP_RIGHT,
                });
            }
        });
    };

    const onSetDefaultAddress = (address, index) => {
        if (!address || addressSaving) {
            return;
        }
        const currentAddresses = Array.isArray(buyerLead?.address_array)
            ? [...buyerLead.address_array]
            : [];
        if (!currentAddresses[index]) {
            return;
        }
        const updatedAddresses = applyDefaultIndex(currentAddresses, index);
        const leadId = buyerLead?.id || id;

        // Optimistic update — reflect the new default immediately
        setBuyerLead((prev) => (prev ? {...prev, address_array: updatedAddresses} : prev));

        if (!leadId) {
            toast.success("Default address updated successfully", {
                position: toast.POSITION.TOP_RIGHT,
            });
            return;
        }
        const payload = {
            id: leadId,
            formData: updatedAddresses,
            type: 2,
        };
        setAddressSaving(true);
        dispatch(addBuyerLead({...payload})).then((res) => {
            setAddressSaving(false);
            if (!res?.error) {
                toast.success("Default address updated successfully", {
                    position: toast.POSITION.TOP_RIGHT,
                });
            } else {
                // Revert the optimistic update on failure
                setBuyerLead((prev) => (prev ? {...prev, address_array: currentAddresses} : prev));
                toast.error(res?.payload?.data || "Failed to update default address", {
                    position: toast.POSITION.TOP_RIGHT,
                });
            }
        });
    };

    const onDeleteAddress = (address, index) => {
        if (!address || addressSaving) {
            return;
        }
        SweetAlert.fire({
            title: "Are you sure?",
            text: "Once deleted, you will not be able to recover this Record!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, Delete",
            cancelButtonText: "No, cancel",
            reverseButtons: true,
        }).then((result) => {
            if (!result.isConfirmed) {
                return;
            }
            const currentAddresses = Array.isArray(buyerLead?.address_array)
                ? [...buyerLead.address_array]
                : [];
            if (!currentAddresses[index]) {
                return;
            }
            const updatedAddresses = currentAddresses.filter((_, itemIndex) => itemIndex !== index);
            const defaultIndex = resolveDefaultIndex(updatedAddresses);
            const normalizedAddresses =
                defaultIndex >= 0 ? applyDefaultIndex(updatedAddresses, defaultIndex) : updatedAddresses;
            const leadId = buyerLead?.id || id;
            if (!leadId) {
                setBuyerLead((prev) =>
                    prev
                        ? {
                              ...prev,
                              address_array: normalizedAddresses,
                          }
                        : prev
                );
                updateCopiedIndexesAfterRemoval(index);
                toast.success("Address deleted successfully", {
                    position: toast.POSITION.TOP_RIGHT,
                });
                return;
            }
            const payload = {
                id: leadId,
                formData: normalizedAddresses,
                type: 2,
            };
            setAddressSaving(true);
            dispatch(addBuyerLead({...payload})).then((res) => {
                setAddressSaving(false);
                if (!res?.error) {
                    setBuyerLead((prev) =>
                        prev
                            ? {
                                  ...prev,
                                  address_array: normalizedAddresses,
                              }
                            : prev
                    );
                    updateCopiedIndexesAfterRemoval(index);
                    toast.success("Address deleted successfully", {
                        position: toast.POSITION.TOP_RIGHT,
                    });
                } else {
                    toast.error(res?.payload?.data || "Failed to delete address", {
                        position: toast.POSITION.TOP_RIGHT,
                    });
                }
            });
        });
    };

    const closeAddressDialog = () => {
        dispatch(closeNewAddressDialog());
    };

    const onSubmitAddressForm = (dialog, data) => {
        const addressIndex = dialog?.data?.addressIndex;
        const currentAddresses = Array.isArray(buyerLead?.address_array)
            ? [...buyerLead.address_array]
            : [];
        const isNewAddress =
            dialog?.type === "new" || addressIndex === undefined || addressIndex === null;

        if (isNewAddress) {
            const hasDefault = currentAddresses.some(
                (address) =>
                    address?.is_default === 1 ||
                    address?.is_default === "1" ||
                    address?.default === 1 ||
                    address?.default === "1" ||
                    address?.is_default_address === 1 ||
                    address?.is_default_address === "1"
            );
            const newAddress = normalizeDefaultFlags(data, !hasDefault);
            const updatedAddresses = [...currentAddresses, newAddress];
            const normalizedAddresses = !hasDefault
                ? applyDefaultIndex(updatedAddresses, updatedAddresses.length - 1)
                : updatedAddresses;

            const leadId = buyerLead?.id || id;
            if (!leadId) {
                setBuyerLead((prev) =>
                    prev
                        ? {
                              ...prev,
                              address_array: normalizedAddresses,
                          }
                        : prev
                );
                toast.success("Address added successfully", {
                    position: toast.POSITION.TOP_RIGHT,
                });
                closeAddressDialog();
                return;
            }
            const payload = {
                id: leadId,
                formData: normalizedAddresses,
                type: 2,
            };
            setAddressSaving(true);
            dispatch(addBuyerLead({...payload})).then((res) => {
                setAddressSaving(false);
                if (!res?.error) {
                    setBuyerLead((prev) =>
                        prev
                            ? {
                                  ...prev,
                                  address_array: normalizedAddresses,
                              }
                            : prev
                    );
                    toast.success("Address added successfully", {
                        position: toast.POSITION.TOP_RIGHT,
                    });
                    closeAddressDialog();
                } else {
                    toast.error(res?.payload?.data || "Failed to add address", {
                        position: toast.POSITION.TOP_RIGHT,
                    });
                }
            });
            return;
        }

        if (!currentAddresses[addressIndex]) {
            closeAddressDialog();
            return;
        }
        const {addressIndex: _discarded, ...existingAddress} = currentAddresses[addressIndex];
        const updatedAddress = {
            ...existingAddress,
            ...data,
        };
        currentAddresses[addressIndex] = updatedAddress;

        const leadId = buyerLead?.id || id;
        if (!leadId) {
            setBuyerLead((prev) => (prev ? {...prev, address_array: currentAddresses} : prev));
            closeAddressDialog();
            return;
        }
        const payload = {
            id: leadId,
            formData: currentAddresses,
            type: 2,
        };
        setAddressSaving(true);
        dispatch(addBuyerLead({...payload})).then((res) => {
            setAddressSaving(false);
            if (!res?.error) {
                setBuyerLead((prev) =>
                    prev
                        ? {
                              ...prev,
                              address_array: currentAddresses,
                          }
                        : prev
                );
                toast.success("Address updated successfully", {
                    position: toast.POSITION.TOP_RIGHT,
                });
                closeAddressDialog();
            } else {
                toast.error(res?.payload?.data || "Failed to update address", {
                    position: toast.POSITION.TOP_RIGHT,
                });
            }
        });
    };

    const isAddressArchived = (address) =>
        address?.is_archived === 1 ||
        address?.is_archived === "1" ||
        address?.archived === 1 ||
        address?.archived === "1" ||
        address?.is_deleted === 1 ||
        address?.is_deleted === "1";

    const getAddressTitle = (address) => {
        return (
            address?.title ||
            address?.label ||
            address?.type ||
            address?.address_type ||
            (address?.is_default === 1 || address?.is_default === "1" ? "Default Address" : "Address")
        );
    };

    const getAddressSubtitle = (address) => {
        if (isAddressArchived(address)) {
            return "No longer in use";
        }
        if (address?.is_default === 1 || address?.is_default === "1") {
            return "Primary address";
        }
        return "Alternative address";
    };

    const getAddressBadges = (address) => {
        const badges = [];
        if (address?.is_default === 1 || address?.is_default === "1") {
            badges.push({label: "Default", color: "success"});
        }
        const typeValue = String(
            address?.type ||
                address?.address_type ||
                address?.addressType ||
                ""
        ).toLowerCase();
        if (typeValue.includes("shipping") || address?.is_shipping === 1 || address?.is_shipping === true) {
            badges.push({label: "Shipping", color: "primary"});
        }
        if (typeValue.includes("billing") || address?.is_billing === 1 || address?.is_billing === true) {
            badges.push({label: "Billing", color: "info"});
        }
        if (isAddressArchived(address)) {
            badges.push({label: "Archived", color: "secondary"});
        }
        return badges;
    };

    const getAddressDates = (address) => {
        const added =
            address?.added_at ||
            address?.created_at ||
            address?.createdAt ||
            address?.created_date;
        const lastUsed =
            address?.last_used_at ||
            address?.last_used ||
            address?.updated_at ||
            address?.updatedAt ||
            address?.updated_date;
        return {added, lastUsed};
    };

    const toNumber = (value, fallback = null) => {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : fallback;
    };

    const membershipTier =
        buyerLead?.membership_tier ||
        buyerLead?.tier_name ||
        buyerLead?.tier ||
        (isVipMember ? "VIP Member" : "-");
    const membershipSince =
        buyerLead?.membership_since ||
        buyerLead?.tier_since ||
        buyerLead?.vip_started_at ||
        buyerLead?.vip_start_date ||
        memberSince;
    const nextTier = buyerLead?.next_tier || buyerLead?.next_tier_name || "-";
    const tierProgressCurrent = toNumber(
        buyerLead?.tier_progress_current ?? buyerLead?.tier_spend
    );
    const tierProgressTarget = toNumber(
        buyerLead?.tier_progress_target ?? buyerLead?.tier_target
    );
    const hasTierProgress =
        tierProgressCurrent !== null && tierProgressTarget !== null && tierProgressTarget > 0;
    const tierProgressPercent =
        hasTierProgress
            ? Math.min(100, Math.round((tierProgressCurrent / tierProgressTarget) * 100))
            : 0;
    const tierProgressLabel =
        hasTierProgress ? `£${tierProgressCurrent} / £${tierProgressTarget}` : "-";

    const pointsBalance = toNumber(buyerLead?.points_balance ?? buyerLead?.points);
    const pointsValue =
        buyerLead?.points_value ??
        buyerLead?.points_value_amount ??
        null;
    const pointsEarned = toNumber(
        buyerLead?.points_earned ?? buyerLead?.points_lifetime
    );
    const pointsRedeemed = toNumber(
        buyerLead?.points_redeemed ?? buyerLead?.points_redeemed_lifetime
    );

    const referralsCount = toNumber(buyerLead?.referrals_count ?? buyerLead?.referrals);
    const referralCode = buyerLead?.referral_code || buyerLead?.referral || "-";



    const availableRewards = Array.isArray(buyerLead?.available_rewards)
        ? buyerLead.available_rewards
        : [];

    const pointsBalanceDisplay =
        pointsBalance !== null ? pointsBalance.toLocaleString() : "-";
    const pointsValueDisplay =
        pointsValue !== null && pointsValue !== undefined && pointsValue !== "" ? pointsValue : "-";
    const pointsEarnedDisplay =
        pointsEarned !== null ? pointsEarned.toLocaleString() : "-";
    const pointsRedeemedDisplay =
        pointsRedeemed !== null ? pointsRedeemed.toLocaleString() : "-";
    const referralsCountDisplay =
        referralsCount !== null ? referralsCount : "-";
    const pointsBalanceLabel =
        pointsBalanceDisplay === "-" ? "-" : `${pointsBalanceDisplay} Points`;
    const pointsValueLabel =
        pointsValueDisplay === "-" ? "Rewards value unavailable" : `= £${pointsValueDisplay} in rewards`;
    const referralsLabel =
        referralsCountDisplay === "-" ? "-" : `${referralsCountDisplay} Friends`;

    const sizeProfile = buyerLead?.size_profile || buyerLead?.sizing_profile || {};
    const preferredSize =
        sizeProfile?.preferred_size || buyerLead?.preferred_size || "-";
    const preferredSizeConfidence =
        sizeProfile?.confidence ?? buyerLead?.preferred_size_confidence ?? null;
    const preferredSizeOrders =
        sizeProfile?.order_count ?? buyerLead?.size_orders_count ?? null;
    const sizeDistribution = Array.isArray(sizeProfile?.distribution)
        ? sizeProfile.distribution
        : Array.isArray(buyerLead?.size_distribution)
            ? buyerLead.size_distribution
            : [];

    const measurementSource = sizeProfile?.measurements || buyerLead?.measurements || {};
    const getMeasurementValue = (key) => {
        const value =
            measurementSource?.[key] ||
            measurementSource?.[`${key}_in`] ||
            measurementSource?.[`${key}_inch`];
        const cmValue =
            measurementSource?.[`${key}_cm`] ||
            measurementSource?.[`${key}_centimeter`];
        if (value || cmValue) {
            const inchLabel = value ? `${value}"` : "-";
            const cmLabel = cmValue ? `${cmValue} cm` : "-";
            return {inchLabel, cmLabel};
        }
        return {inchLabel: "-", cmLabel: "-"};
    };

    const fitPreference = sizeProfile?.fit_preference || buyerLead?.fit_preference || "-";
    const bodyShape = sizeProfile?.body_shape || buyerLead?.body_shape || "-";

    const fitReturns = Array.isArray(sizeProfile?.fit_returns)
        ? sizeProfile.fit_returns
        : Array.isArray(buyerLead?.fit_returns)
            ? buyerLead.fit_returns
            : [];

    const aiRecommendation = sizeProfile?.recommendation || buyerLead?.size_recommendation || {};
    const aiRecommendedSize = aiRecommendation?.size || aiRecommendation?.recommended_size || "-";
    const aiRecommendedNote = aiRecommendation?.note || aiRecommendation?.description || "-";
    const aiRecommendedWhy = aiRecommendation?.reason || aiRecommendation?.why || "-";

    const sizingLastUpdated =
        sizeProfile?.updated_at ||
        sizeProfile?.updatedAt ||
        buyerLead?.sizing_updated_at ||
        buyerLead?.sizingUpdatedAt ||
        null;

    const orderStats = buyerLead?.order_stats || buyerLead?.orders_summary || {};
    const totalOrders =
        orderStats?.total_orders ?? buyerLead?.total_orders ?? null;
    const totalOrderValue =
        orderStats?.total_value ?? buyerLead?.total_order_value ?? null;
    const averageOrderValue =
        orderStats?.average_value ?? buyerLead?.average_order_value ?? null;
    const lifetimeValue =
        orderStats?.lifetime_value ?? buyerLead?.lifetime_value ?? null;

    const returnRate =
        orderStats?.return_rate ?? buyerLead?.return_rate ?? null;
    const refundsTotal =
        orderStats?.refunds_total ?? buyerLead?.refunds_total ?? null;
    const roiPerUser =
        orderStats?.roi_per_user ?? buyerLead?.roi_per_user ?? null;

    const topProducts = Array.isArray(buyerLead?.top_products)
        ? buyerLead.top_products
        : Array.isArray(buyerLead?.top_purchased_products)
            ? buyerLead.top_purchased_products
            : [];
    const categoryPreferences = Array.isArray(buyerLead?.category_preferences)
        ? buyerLead.category_preferences
        : Array.isArray(buyerLead?.category_stats)
            ? buyerLead.category_stats
            : [];

    const abandonedCart = buyerLead?.abandoned_cart || null;
    const abandonedCartItems = Array.isArray(abandonedCart?.items)
        ? abandonedCart.items
        : [];
    const shoppingPreferences = buyerLead?.shopping_preferences || {};
    const priceRangePreference =
        shoppingPreferences?.price_range || shoppingPreferences?.price_range_affinity || "-";
    const favoriteBrand =
        shoppingPreferences?.favorite_brand || shoppingPreferences?.brand || "-";
    const colorPreferences = Array.isArray(shoppingPreferences?.colors)
        ? shoppingPreferences.colors
        : [];

    const supportCases = Array.isArray(buyerLead?.support_cases)
        ? buyerLead.support_cases
        : Array.isArray(buyerLead?.cases)
            ? buyerLead.cases
            : [];
    const supportStats = buyerLead?.support_stats || {};
    const hasSupportData = supportCases.length > 0 || Object.keys(supportStats).length > 0;
    const totalCases =
        supportStats?.total ?? supportCases.length;
    const openCases =
        supportStats?.open ??
        supportCases.filter((item) => {
            const status = String(item?.status || "").toLowerCase();
            return status.includes("open") || status.includes("progress") || status.includes("pending");
        }).length;
    const resolvedCases =
        supportStats?.resolved ??
        supportCases.filter((item) => {
            const status = String(item?.status || "").toLowerCase();
            return status.includes("resolved") || status.includes("closed");
        }).length;

    const feedbackData = buyerLead?.feedback || {};
    const categoryInterests = Array.isArray(feedbackData?.categories)
        ? feedbackData.categories
        : Array.isArray(buyerLead?.category_interests)
            ? buyerLead.category_interests
            : [];
    const sentimentValue =
        feedbackData?.sentiment || buyerLead?.sentiment || "-";
    const feedbackCount =
        feedbackData?.count ?? buyerLead?.feedback_count ?? null;

    const reviewData = buyerLead?.reviews || {};
    const averageRating =
        reviewData?.average_rating ?? buyerLead?.average_rating ?? null;
    const reviewCount =
        reviewData?.count ?? buyerLead?.review_count ?? null;
    const ugcContributions =
        reviewData?.ugc_count ?? buyerLead?.ugc_count ?? null;

    const socialConnections = Array.isArray(buyerLead?.social_connections)
        ? buyerLead.social_connections
        : [];
    const contactPreferences = buyerLead?.contact_preferences || {};
    const preferredChannel =
        contactPreferences?.channel || contactPreferences?.preferred_channel || "-";
    const bestContactTime =
        contactPreferences?.best_time || contactPreferences?.time || "-";

    const aiUsage = buyerLead?.ai_usage || buyerLead?.ai_metrics || {};
    const aiTotalQuestions =
        aiUsage?.total_questions ?? buyerLead?.ai_questions ?? null;
    const aiLast30Days =
        aiUsage?.last_30_days ?? buyerLead?.ai_questions_last_30 ?? null;
    const aiLifetimeCost =
        aiUsage?.lifetime_cost ?? buyerLead?.ai_cost ?? null;
    const aiImagesGenerated =
        aiUsage?.images_generated ?? buyerLead?.ai_images_generated ?? null;

    const aiToolUsage = Array.isArray(aiUsage?.tools)
        ? aiUsage.tools
        : Array.isArray(buyerLead?.ai_tools)
            ? buyerLead.ai_tools
            : [];
    const aiCostBreakdown = Array.isArray(aiUsage?.cost_breakdown)
        ? aiUsage.cost_breakdown
        : Array.isArray(buyerLead?.ai_cost_breakdown)
            ? buyerLead.ai_cost_breakdown
            : [];
    const aiGeneratedImages = Array.isArray(aiUsage?.generated_images)
        ? aiUsage.generated_images
        : Array.isArray(buyerLead?.ai_generated_images)
            ? buyerLead.ai_generated_images
            : [];

    const marketingHistory = buyerLead?.marketing_history || buyerLead?.marketing_stats || {};
    const emailsReceived = marketingHistory?.emails_received ?? marketingHistory?.emails ?? null;
    const emailOpenRate = marketingHistory?.open_rate ?? marketingHistory?.email_open_rate ?? null;
    const smsSent = marketingHistory?.sms_sent ?? marketingHistory?.sms ?? null;
    const smsDeliveryRate = marketingHistory?.delivery_rate ?? marketingHistory?.sms_delivery_rate ?? null;
    const acquisitionSource =
        marketingHistory?.acquisition_source ?? marketingHistory?.source ?? "-";
    const acquisitionDate =
        marketingHistory?.acquisition_date ?? marketingHistory?.source_date ?? null;

    const internalNotes = Array.isArray(buyerLead?.internal_notes)
        ? buyerLead.internal_notes
        : [];
    const auditLog = Array.isArray(buyerLead?.audit_log)
        ? buyerLead.audit_log
        : [];
    const activityLogName = buyerLead?.activity_log_name || buyerLead?.log_name || "Buyer Lead";
    const activityLogId = buyerLead?.id || id;

    const currencySymbol =
        buyerLead?.currency_symbol ||
        buyerLead?.currency?.symbol ||
        "£";
    const formatCurrency = (value) => {
        if (value === null || value === undefined || value === "") {
            return "-";
        }
        const num = Number(value);
        if (!Number.isFinite(num)) {
            return value;
        }
        const formatted = num.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
        return `${currencySymbol}${formatted}`;
    };

    const scrollToSection = (sectionId) => {
        if (typeof document === "undefined" || typeof window === "undefined") {
            return;
        }
        const target = document.getElementById(`buyer-lead-section-${sectionId}`);
        if (!target) {
            return;
        }
        const scrollEl = scrollContainerRef.current;
        const headerEl = headerRef.current;
        const computedStyles = scrollEl ? window.getComputedStyle(scrollEl) : null;
        const stickyOffset = computedStyles
            ? parseFloat(computedStyles.getPropertyValue("--buyer-lead-sticky-offset")) || 0
            : 0;
        const headerHeight = computedStyles
            ? parseFloat(computedStyles.getPropertyValue("--buyer-lead-header-height")) || 0
            : 0;
        const fallbackHeaderHeight = headerEl ? headerEl.getBoundingClientRect().height || 0 : 0;
        const totalOffset = stickyOffset + (headerHeight || fallbackHeaderHeight) + 16;
        const targetTop = target.getBoundingClientRect().top + window.pageYOffset - totalOffset;
        
        isScrollingProgrammaticallyRef.current = true;
        window.scrollTo({behavior: "smooth", top: Math.max(0, targetTop)});
        
        setTimeout(() => {
            isScrollingProgrammaticallyRef.current = false;
        }, 800);
    };

    const formatAddress = (address) => {
        const lines = [
            address?.address_1 || address?.address1 || address?.address_line_1,
            address?.address_2 || address?.address2 || address?.address_line_2,
            address?.city || address?.town,
            address?.zip || address?.postcode,
            address?.country?.name || address?.country?.label || address?.country
        ]
            .map((line) => (line && typeof line === "string" ? line.trim() : line))
            .filter(Boolean);
        return lines.length > 0 ? lines : ["-"];
    };

    const formatDate = (value, fallback = "-") => {
        if (!value) {
            return fallback;
        }
        const parsed = moment(value);
        return parsed.isValid() ? parsed.format("DD MMM YYYY") : fallbackValue(value);
    };

    const formatDateTime = (value, fallback = "-") => {
        if (!value) {
            return fallback;
        }
        const parsed = moment(value);
        return parsed.isValid() ? parsed.format("DD MMM YYYY, hh:mm A") : fallbackValue(value);
    };

    const formatDob = (value) => {
        if (!value) {
            return "-";
        }
        const parsed = moment(value);
        if (!parsed.isValid()) {
            return fallbackValue(value);
        }
        const age = moment().diff(parsed, "years");
        return `${parsed.format("DD MMM YYYY")} (Age: ${age})`;
    };

    const handleAvatarUpload = (file) => {
        if (!file || avatarUploading) {
            return;
        }
        if (file.type && !file.type.startsWith("image/")) {
            toast.error("Please upload a valid image file.", {
                position: toast.POSITION.TOP_RIGHT,
            });
            return;
        }
        const leadId = buyerLead?.id || id;
        if (!leadId) {
            toast.error("Unable to upload image without a buyer lead.", {
                position: toast.POSITION.TOP_RIGHT,
            });
            return;
        }
        setAvatarUploading(true);
        dispatch(uploadBuyerLeadProfileImage({id: leadId, file}))
            .then((res) => {
                if (!res?.error && !res?.payload?.error) {
                    const payload = res?.payload || {};
                    const uploadedUrl = payload.profile_image || payload.image || payload.image_url;
                    setBuyerLead((prev) =>
                        prev
                            ? {
                                  ...prev,
                                  avatar: uploadedUrl || prev.avatar,
                                  photo_url: uploadedUrl || prev.photo_url,
                                  profile_image: uploadedUrl || prev.profile_image,
                                  image: uploadedUrl || prev.image,
                                  image_path: payload.image_path || prev.image_path,
                              }
                            : prev
                    );
                    toast.success("Profile image updated successfully.", {
                        position: toast.POSITION.TOP_RIGHT,
                    });
                } else {
                    const message =
                        res?.payload?.data ||
                        res?.payload ||
                        "Failed to upload profile image. Please try again.";
                    toast.error(message, {
                        position: toast.POSITION.TOP_RIGHT,
                    });
                }
            })
            .finally(() => {
                setAvatarUploading(false);
            });
    };

    const handleAvatarRemove = () => {
        if (avatarUploading || !hasCustomAvatar) {
            return;
        }
        const leadId = buyerLead?.id || id;
        if (!leadId) {
            toast.error("Unable to remove image without a buyer lead.", {
                position: toast.POSITION.TOP_RIGHT,
            });
            return;
        }
        SweetAlert.fire({
            title: "Are you sure?",
            text: "This will remove the current profile image.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, Delete",
            cancelButtonText: "No, cancel",
            reverseButtons: true,
        }).then((result) => {
            if (!result.isConfirmed) {
                return;
            }
            setAvatarUploading(true);
            dispatch(deleteBuyerLeadProfileImage({id: leadId}))
                .then((res) => {
                    if (!res?.error && !res?.payload?.error) {
                        setBuyerLead((prev) =>
                            prev
                                ? {
                                      ...prev,
                                      avatar: null,
                                      photo_url: null,
                                      profile_image: null,
                                      image: null,
                                      image_path: null,
                                  }
                                : prev
                        );
                        toast.success("Profile image removed successfully.", {
                            position: toast.POSITION.TOP_RIGHT,
                        });
                    } else {
                        const message =
                            res?.payload?.data ||
                            res?.payload ||
                            "Failed to remove profile image. Please try again.";
                        toast.error(message, {
                            position: toast.POSITION.TOP_RIGHT,
                        });
                    }
                })
                .finally(() => {
                    setAvatarUploading(false);
                });
        });
    };

    const onEdit = () => {
        if (buyerLead?.id || id) {
            setIsEditMode((prev) => !prev);
        }
    };

    const handleEditCancel = () => {
        setIsEditMode(false);
    };

    const handleEditComplete = () => {
        setIsEditMode(false);
        loadBuyerLead();
    };

    return (
        <Fragment>
            {loading ? (
                <PreLoader />
            ) : (
                <>
                    <Breadcrumb data={dataMenu} title="Buyer Lead Details" />
                    <Container fluid={true}>
                        {activityLogId && (
                            <div className="buyer-lead-activity-log">
                                <LogActivity log={activityLogName} id={activityLogId} />
                            </div>
                        )}
                        <div className="buyer-lead-scroll" ref={scrollContainerRef}>
                            <div className="buyer-lead-header-wrapper" ref={headerRef}>
                                <BuyerLeadHeader
                                    avatarUrl={avatarUrl}
                                    fullName={fullName}
                                    leadCode={leadCode}
                                    email={emailValue}
                                    phone={phoneValue}
                                    memberSince={memberSince}
                                    accountStatus={accountStatus}
                                    summaryBadges={summaryBadges}
                                    lastUpdated={lastUpdated}
                                    onEdit={onEdit}
                                    onAvatarUpload={handleAvatarUpload}
                                    onAvatarRemove={handleAvatarRemove}
                                    isAvatarUploading={avatarUploading}
                                    canUploadAvatar={Boolean((buyerLead?.id || id) && hasViewPermission)}
                                    canRemoveAvatar={Boolean((buyerLead?.id || id) && hasViewPermission && hasCustomAvatar)}
                                    formatDate={formatDate}
                                    formatDateTime={formatDateTime}
                                    activeTab={activeTab}
                                    setActiveTab={setActiveTab}
                                    scrollToSection={scrollToSection}
                                    isEditMode={isEditMode}
                                />
                            </div>
                            {isEditMode ? (
                                <div className="buyer-lead-edit-mode">
                                    <UpdatePrimaryBuyerLead
                                        onCancel={handleEditCancel}
                                        onComplete={handleEditComplete}
                                        showBreadcrumb={false}
                                    />
                                </div>
                            ) : (
                                <>
                                    <BuyerLeadProfile
                                        fullName={fullName}
                                        email={emailValue}
                                        phone={phoneValue}
                                        dobValue={dobValue}
                                        formatDob={formatDob}
                                        tags={tags}
                                        accountStatus={accountStatus}
                                        emailConsent={emailConsent}
                                        emailConsentDate={emailConsentDate}
                                        smsConsent={smsConsent}
                                        smsConsentDate={smsConsentDate}
                                        pushConsent={pushConsent}
                                        formatDate={formatDate}
                                        addresses={addresses}
                                        formatAddress={formatAddress}
                                        getAddressBadges={getAddressBadges}
                                        getAddressTitle={getAddressTitle}
                                        getAddressSubtitle={getAddressSubtitle}
                                        isAddressArchived={isAddressArchived}
                                        getAddressDates={getAddressDates}
                                        copiedAddressIndexes={copiedAddressIndexes}
                                        onAddAddress={onAddAddress}
                                        onEditAddress={onEditAddress}
                                        onCopyAddress={onCopyAddress}
                                        onSetDefaultAddress={onSetDefaultAddress}
                                        onDeleteAddress={onDeleteAddress}
                                        membershipTier={membershipTier}
                                        membershipSince={membershipSince}
                                        isVipMember={isVipMember}
                                        vipValidityDate={vipValidityDate}
                                        nextTier={nextTier}
                                        tierProgressPercent={tierProgressPercent}
                                        tierProgressLabel={tierProgressLabel}
                                        pointsBalanceLabel={pointsBalanceLabel}
                                        pointsValueLabel={pointsValueLabel}
                                        pointsEarnedDisplay={pointsEarnedDisplay}
                                        pointsRedeemedDisplay={pointsRedeemedDisplay}
                                        referralsLabel={referralsLabel}
                                        referralCode={referralCode}

                                        availableRewards={availableRewards}
                                        preferredSize={preferredSize}
                                        preferredSizeConfidence={preferredSizeConfidence}
                                        preferredSizeOrders={preferredSizeOrders}
                                        sizeDistribution={sizeDistribution}
                                        fitReturns={fitReturns}
                                        getMeasurementValue={getMeasurementValue}
                                        sizingLastUpdated={sizingLastUpdated}
                                        fitPreference={fitPreference}
                                        bodyShape={bodyShape}
                                        aiRecommendedSize={aiRecommendedSize}
                                        aiRecommendedNote={aiRecommendedNote}
                                        aiRecommendedWhy={aiRecommendedWhy}
                                    />
                                    <BuyerLeadCommerce
                                        totalOrders={totalOrders}
                                        totalOrderValue={totalOrderValue}
                                        averageOrderValue={averageOrderValue}
                                        lifetimeValue={lifetimeValue}
                                        returnRate={returnRate}
                                        refundsTotal={refundsTotal}
                                        roiPerUser={roiPerUser}
                                        formatCurrency={formatCurrency}
                                        orderHistory={orderHistory}
                                        formatDate={formatDate}
                                        topProducts={topProducts}
                                        categoryPreferences={categoryPreferences}
                                        abandonedCart={abandonedCart}
                                        abandonedCartItems={abandonedCartItems}
                                        formatDateTime={formatDateTime}
                                        priceRangePreference={priceRangePreference}
                                        favoriteBrand={favoriteBrand}
                                        colorPreferences={colorPreferences}
                                    />
                                    <BuyerLeadEngagement
                                        supportCases={supportCases}
                                        hasSupportData={hasSupportData}
                                        totalCases={totalCases}
                                        openCases={openCases}
                                        resolvedCases={resolvedCases}
                                        formatDate={formatDate}
                                        formatDateTime={formatDateTime}
                                        categoryInterests={categoryInterests}
                                        sentimentValue={sentimentValue}
                                        feedbackCount={feedbackCount}
                                        emailsReceived={emailsReceived}
                                        emailOpenRate={emailOpenRate}
                                        smsSent={smsSent}
                                        smsDeliveryRate={smsDeliveryRate}
                                        acquisitionSource={acquisitionSource}
                                        acquisitionDate={acquisitionDate}
                                        averageRating={averageRating}
                                        reviewCount={reviewCount}
                                        ugcContributions={ugcContributions}
                                        socialConnections={socialConnections}
                                        preferredChannel={preferredChannel}
                                        bestContactTime={bestContactTime}
                                        aiTotalQuestions={aiTotalQuestions}
                                        aiLast30Days={aiLast30Days}
                                        aiLifetimeCost={aiLifetimeCost}
                                        aiImagesGenerated={aiImagesGenerated}
                                        formatCurrency={formatCurrency}
                                        aiToolUsage={aiToolUsage}
                                        aiCostBreakdown={aiCostBreakdown}
                                        aiGeneratedImages={aiGeneratedImages}
                                        internalNotes={internalNotes}
                                        auditLog={auditLog}
                                    />
                                </>
                            )}
                        </div>
                    </Container>
                    {!isEditMode && (
                        <ModalFormSubmit
                            defaultValues={defaultAddressValues}
                            schema={addressSchema}
                            onSubmitForm={onSubmitAddressForm}
                            reset={addressReset}
                            formState={addressFormState}
                            handleSubmit={addressHandleSubmit}
                            title="Buyer Lead Address"
                            dialog={buyerLeadState.addressAddDialog}
                            closeDialog={closeAddressDialog}
                            setValue={addressSetValue}
                            getValues={addressGetValues}
                            isUseSelector
                            size="xl"
                            publish={false}
                            buttonDisable={addressSaving}
                        >
                            <BuyerLeadAddressForm
                                countries={countries}
                                control={addressControl}
                                formText="Buyer Lead Address"
                                errors={addressErrors}
                                register={addressRegister}
                                setValue={addressSetValue}
                                trigger={addressTrigger}
                                getValues={addressGetValues}
                            />
                        </ModalFormSubmit>
                    )}
                </>
            )}
        </Fragment>
    );
};

export default withReducer([{lead: leadReducer}])(ViewBuyerLead);
